/**
 * NotesContext — CEO notes and the staff comment thread per client.
 * Seeded for a few featured accounts; comments are addable in-session.
 * Live wiring: read/write a `clientNotes` subcollection in Firestore.
 */
import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';

export interface CeoNote { id: string; clientId: string; author: string; text: string; time: string; pinned?: boolean; }
export interface Comment { id: string; clientId: string; author: string; text: string; time: string; }

const SEED_NOTES: CeoNote[] = [
  { id: 'cn1', clientId: '*', author: 'CEO', text: 'Treat every sample request as a revenue signal — fast, accurate samples win the reorder. Confirm SKUs before logistics moves.', time: 'Pinned', pinned: true },
];

const Ctx = createContext<{
  notesFor: (clientId: string) => CeoNote[];
  commentsFor: (clientId: string) => Comment[];
  addComment: (clientId: string, author: string, text: string) => void;
  addNote: (clientId: string, author: string, text: string) => void;
} | null>(null);

export function NotesProvider({ children }: { children: ReactNode }) {
  const [notes, setNotes] = useState<CeoNote[]>(SEED_NOTES);
  const [comments, setComments] = useState<Comment[]>([
    { id: 'c1', clientId: '*', author: 'Fatima (CSR)', text: 'Client prefers email over phone. Decision-maker is the buyer, not the contact on file.', time: 'Yesterday' },
  ]);

  const notesFor = useCallback((clientId: string) => notes.filter((n) => n.clientId === clientId || n.clientId === '*'), [notes]);
  const commentsFor = useCallback((clientId: string) => comments.filter((c) => c.clientId === clientId || c.clientId === '*'), [comments]);
  const addComment = useCallback((clientId: string, author: string, text: string) => {
    setComments((c) => [...c, { id: `c${Date.now()}`, clientId, author, text, time: 'Just now' }]);
  }, []);
  const addNote = useCallback((clientId: string, author: string, text: string) => {
    setNotes((n) => [{ id: `cn${Date.now()}`, clientId, author, text, time: 'Just now' }, ...n]);
  }, []);

  return <Ctx.Provider value={{ notesFor, commentsFor, addComment, addNote }}>{children}</Ctx.Provider>;
}

export function useNotes() {
  const c = useContext(Ctx);
  if (!c) throw new Error('useNotes must be used within NotesProvider');
  return c;
}
